/**
 * "Needs your eyes" data for the admin dashboard — the same badge counts that
 * drive the sidebar (F60b `NavCountsProvider`, one shared poll) as a single
 * sorted worklist. The API returns only the counts the viewer's permissions
 * allow, and each row is gated on the same permission, so the list reflects
 * the actor's role.
 */
import {
  ADMIN_NAV_COUNT_PERMISSIONS,
  type AdminNavCountKey,
  type Permission,
} from '@bravemove/types';
import { useNavCounts } from '../navCounts/useNavCount';

export interface DashboardQueueRow {
  readonly id: string;
  /** i18n key for the queue's display label (reuses the nav labels). */
  readonly labelKey: string;
  readonly count: number;
  readonly route: string;
}

export function useDashboardQueues(
  permissions: readonly Permission[],
): readonly DashboardQueueRow[] {
  const { counts } = useNavCounts();
  const count = (key: AdminNavCountKey): number =>
    permissions.includes(ADMIN_NAV_COUNT_PERMISSIONS[key]) ? (counts[key] ?? 0) : 0;

  const rows: readonly DashboardQueueRow[] = [
    {
      id: 'verifications',
      labelKey: 'admin:verification.nav.verifications',
      count: count('verifications'),
      route: '/verifications',
    },
    {
      id: 'venue-claims',
      labelKey: 'admin:claim.nav.venue_claims',
      count: count('venueClaims'),
      route: '/venue-claims',
    },
    {
      id: 'venue-submissions',
      labelKey: 'admin:venue.nav.submissions',
      count: count('venueSubmissions'),
      route: '/venues/submissions',
    },
    {
      id: 'venue-edits',
      labelKey: 'admin:venue.nav.edits',
      count: count('venueEdits'),
      route: '/venues/edits',
    },
    {
      id: 'venue-photos',
      labelKey: 'admin:venue-editor.nav.venuePhotos',
      count: count('venuePhotos'),
      route: '/venue-photos',
    },
    {
      id: 'payment-disputes',
      labelKey: 'admin:nav.paymentDisputes',
      count: count('paymentDisputes'),
      route: '/payment-disputes',
    },
    {
      id: 'commission-statements',
      labelKey: 'admin:commission.sidebar.statements',
      count: count('commissionStatements'),
      route: '/commission-statements',
    },
    {
      id: 'finance',
      labelKey: 'admin:nav.finance',
      count: count('financeReconciliation'),
      route: '/finance',
    },
    {
      id: 'reports',
      labelKey: 'admin:f43.nav.reports',
      count: count('openReports'),
      route: '/reports',
    },
    { id: 'gdpr', labelKey: 'admin:f43.nav.gdpr', count: count('pendingGdpr'), route: '/gdpr' },
    {
      id: 'trainer-verifications',
      labelKey: 'admin:trainer-verification.nav.trainerVerifications',
      count: count('trainerVerifications'),
      route: '/trainer-verifications',
    },
    {
      id: 'skill-disputes',
      labelKey: 'admin:skill.nav.skillDisputes',
      count: count('skillDisputes'),
      route: '/skill-disputes',
    },
    {
      id: 'open-game-reports',
      labelKey: 'admin:open-game.nav.openGameReports',
      count: count('openGameReports'),
      route: '/open-game-reports',
    },
    {
      id: 'review-moderation',
      labelKey: 'admin:recommendation.nav.moderation',
      count: count('flaggedReviews'),
      route: '/review-moderation',
    },
    {
      id: 'placement-approvals',
      labelKey: 'admin:placement.nav.approvals',
      count: count('placementApprovals'),
      route: '/placement-approvals',
    },
  ];

  return rows.filter((r) => r.count > 0).sort((a, b) => b.count - a.count);
}
